"use client";

import { motion } from "framer-motion";
import { ServicesData } from "@/data/ServicesData";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import Container from "../../wrapper/Container";
import { cn } from "@/lib/utils/utils";

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  scrolled: boolean;
}

const ServicesMegaMenu = ({ isOpen, scrolled }: MegaMenuProps) => {
  return (
    // Always mounted, never conditionally rendered. Previously this lived
    // behind {isOpen && ...}, so the four /services/[slug] links existed only
    // after a hover — meaning they were absent from the server HTML and no
    // crawler ever saw them. Visibility is now driven purely by CSS/variants,
    // so the menu looks and animates exactly as before but the links ship in
    // the markup on every page. Do not put this back behind a mount condition.
    <motion.div
      initial={false}
      animate={isOpen ? "open" : "closed"}
      variants={{
        closed: { height: 0, opacity: 0, pointerEvents: "none" },
        open: { height: "auto", opacity: 1, pointerEvents: "auto" },
      }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      // Hidden from assistive tech while collapsed; links stay crawlable.
      // `inert` as well as `aria-hidden`: collapsed, the panel still has its
      // links in the DOM at height 0, and an aria-hidden subtree holding
      // focusable children is exactly what the a11y audit flags. `inert` takes
      // them out of the tab order instead of only hiding them from the tree.
      aria-hidden={!isOpen}
      inert={!isOpen}
      className={`absolute w-[90vw] max-w-300  rounded-3xl border border-black/5 bg-white z-1001 ${scrolled ? "-left-85" : " left-1/2 -translate-x-1/2"}`}
    >
      <div
        className={cn(
          "absolute -top-[11.5px] w-6 h-6 bg-white rounded-sm rotate-45 z-2000 border-t border-l border-black/10",
          scrolled ? "left-[50%]" : "left-[55%]",
        )}
      />
      <Container className="px-4 lg:px-10 py-12 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {ServicesData.map((service, index) => (
            <motion.div
              key={service.id}
              // Same stagger as before, expressed as variants so it replays
              // on every open instead of firing once at mount.
              variants={{
                closed: { y: 20, opacity: 0 },
                open: {
                  y: 0,
                  opacity: 1,
                  transition: { delay: 0.1 + index * 0.1 },
                },
              }}
              className="group relative"
            >
              <Link
                href={`/services/${service.slug}`}
                className="flex flex-col gap-6"
              >
                {/* ID Number */}
                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-black/20 group-hover:text-primary transition-colors">
                  /{service.id}
                </span>
                {/* Title & Description */}
                <div className="space-y-4">
                  <h3 className="text-xl lg:text-2xl font-black uppercase tracking-tighter leading-none group-hover:translate-x-2 transition-transform duration-500 flex items-center gap-2">
                    {service.title}
                    <ArrowUpRight
                      size={18}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-60">
                    {service.description}
                  </p>
                </div>
                {/* Tags List */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {service.features.map((feature) => (
                    <span
                      key={feature}
                      className="text-[9px] font-bold text-accent-foreground uppercase border border-black/10 px-2 py-1"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* The menu lists the four services but had no route to the index that
            collects them. Without this the /services page is reachable only
            from the header item itself. */}
        <Link
          href="/services"
          className="group mt-12 flex items-center gap-2 border-t border-black/10 pt-6 text-sm font-black uppercase tracking-widest text-black/60 transition-colors hover:text-black"
        >
          View all services
          <ArrowUpRight
            size={16}
            className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
          />
        </Link>
      </Container>

      {/* Subtle Bottom Progress Bar */}
      <div className="absolute bottom-5 left-0 h-0.5 w-full">
        <motion.div
          className="h-full bg-primary mx-auto w-[95%]"
          variants={{
            closed: { scaleX: 0 },
            open: {
              scaleX: 1,
              transition: { duration: 5, ease: "easeInOut" },
            },
          }}
        />
      </div>
    </motion.div>
  );
};

export default ServicesMegaMenu;
